import React, { useState, useEffect } from 'react';
import { XMarkIcon, MapPinIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { apiGet } from '../../utils/api';
import type { FarmField } from '../activities/types';

interface FieldDiaryFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => Promise<void>;
    initialData?: any;
}

const FieldDiaryForm: React.FC<FieldDiaryFormProps> = ({ isOpen, onClose, onSubmit, initialData }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fields, setFields] = useState<FarmField[]>([]);
    const [fieldsLoading, setFieldsLoading] = useState(false);
    const [fieldsError, setFieldsError] = useState('');
    const [locating, setLocating] = useState(false);

    const [formData, setFormData] = useState({
        field_plot_id: '',
        visit_date: new Date().toISOString().split('T')[0],
        visit_type: 'routine',
        location_name: '',
        location_lat: '',
        location_lng: '',
        weather_condition: 'sunny',
        temperature_celsius: '',
        humidity_percent: '',
        crop_type: '',
        growth_stage: 'vegetative',
        health_status: 'good',
        health_score: '75',
        pest_observed: false,
        pest_type: '',
        disease_observed: false,
        disease_type: '',
        severity_level: 'low',
        activities: [] as string[],
        observations: '',
        recommendations: '',
        action_items: '',
        follow_up_required: false,
        follow_up_date: '',
        follow_up_notes: '',
    });

    // Load only the farms and fields the signed-in user is authorized for. The
    // backend rejects a visit without a valid assigned field, so the picker is
    // the contract, not a convenience.
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setFieldsLoading(true);
        setFieldsError('');
        // Read directly rather than through the shared helper, which collapses a
        // failed request into an empty list and would tell the user they have no
        // fields when the network was the problem.
        apiGet<any>('/api/maps/farm-plots')
            .then((data) => {
                if (cancelled) return;
                const plots = Array.isArray(data?.plots)
                    ? data.plots
                    : Array.isArray(data)
                        ? data
                        : [];
                setFields(plots as FarmField[]);
            })
            .catch(() => {
                if (!cancelled) setFieldsError('Your assigned fields could not be loaded. Close this form and try again.');
            })
            .finally(() => {
                if (!cancelled) setFieldsLoading(false);
            });
        return () => { cancelled = true; };
    }, [isOpen]);

    useEffect(() => {
        if (initialData) {
            setFormData({
                field_plot_id: initialData.field_plot_id ? String(initialData.field_plot_id) : '',
                visit_date: initialData.visit_date?.split('T')[0] || new Date().toISOString().split('T')[0],
                visit_type: initialData.visit_type || 'routine',
                location_name: initialData.location_name || '',
                location_lat: initialData.location_lat?.toString() || '',
                location_lng: initialData.location_lng?.toString() || '',
                weather_condition: initialData.weather_condition || 'sunny',
                temperature_celsius: initialData.temperature_celsius?.toString() || '',
                humidity_percent: initialData.humidity_percent?.toString() || '',
                crop_type: initialData.crop_type || '',
                growth_stage: initialData.growth_stage || 'vegetative',
                health_status: initialData.health_status || 'good',
                health_score: initialData.health_score?.toString() || '75',
                pest_observed: initialData.pest_observed || false,
                pest_type: initialData.pest_type || '',
                disease_observed: initialData.disease_observed || false,
                disease_type: initialData.disease_type || '',
                severity_level: initialData.severity_level || 'low',
                activities: initialData.activities || [],
                observations: initialData.observations || '',
                recommendations: initialData.recommendations || '',
                action_items: initialData.action_items || '',
                follow_up_required: initialData.follow_up_required || false,
                follow_up_date: initialData.follow_up_date?.split('T')[0] || '',
                follow_up_notes: initialData.follow_up_notes || '',
            });
        }
    }, [initialData]);

    const selectedField = fields.find((field) => String(field.id) === String(formData.field_plot_id));

    const handleFieldChange = (value: string) => {
        const field = fields.find((item) => String(item.id) === value);
        setFormData((prev) => ({
            ...prev,
            field_plot_id: value,
            // Keep the crop context aligned with the chosen field unless the
            // scout deliberately typed a different crop.
            crop_type: field?.crop_type || prev.crop_type,
            location_name: prev.location_name || field?.name || field?.plot_code || ''
        }));
    };

    const handleUseMyLocation = () => {
        setError('');
        if (!navigator.geolocation) {
            setError('This device cannot share a location. Enter the coordinates manually.');
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setFormData((prev) => ({
                    ...prev,
                    location_lat: position.coords.latitude.toFixed(6),
                    location_lng: position.coords.longitude.toFixed(6)
                }));
                setLocating(false);
            },
            () => {
                setError('Location permission was denied or unavailable. Enter the coordinates manually.');
                setLocating(false);
            },
            { enableHighAccuracy: true, timeout: 15000 }
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (!formData.field_plot_id) {
            setError('Choose the farm field this visit was made on.');
            return;
        }
        setLoading(true);

        try {
            const submitData = {
                ...formData,
                field_plot_id: parseInt(formData.field_plot_id, 10),
                cohort_id: null,
                location_lat: formData.location_lat ? parseFloat(formData.location_lat) : null,
                location_lng: formData.location_lng ? parseFloat(formData.location_lng) : null,
                temperature_celsius: formData.temperature_celsius ? parseFloat(formData.temperature_celsius) : null,
                humidity_percent: formData.humidity_percent ? parseFloat(formData.humidity_percent) : null,
                health_score: formData.health_score ? parseFloat(formData.health_score) : null,
                follow_up_date: formData.follow_up_required && formData.follow_up_date ? formData.follow_up_date : null,
            };
            await onSubmit(submitData);
        } catch (err: any) {
            setError(err.message || 'Failed to save entry');
        } finally {
            setLoading(false);
        }
    };

    const toggleActivity = (activity: string) => {
        setFormData(prev => ({
            ...prev,
            activities: prev.activities.includes(activity)
                ? prev.activities.filter(a => a !== activity)
                : [...prev.activities, activity]
        }));
    };

    const ACTIVITIES = [
        'Watering', 'Fertilizing', 'Pruning', 'Spraying', 'Harvesting',
        'Weeding', 'Mulching', 'Soil Testing', 'Pest Monitoring', 'Training'
    ];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">
                        {initialData ? 'Edit Field Diary Entry' : 'New Field Diary Entry'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Farm field — required by the API */}
                    <div>
                        <label htmlFor="field_plot_id" className="block text-sm font-medium text-gray-700 mb-1">
                            Farm Field *
                        </label>
                        <select
                            id="field_plot_id"
                            value={formData.field_plot_id}
                            onChange={(e) => handleFieldChange(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            disabled={fieldsLoading}
                            required
                        >
                            <option value="">
                                {fieldsLoading ? 'Loading your fields…' : 'Select the field that was visited'}
                            </option>
                            {fields.map((field) => (
                                <option key={field.id} value={field.id}>
                                    {[field.name || field.plot_code || `Field #${field.id}`, field.farmer_name]
                                        .filter(Boolean)
                                        .join(' — ')}
                                </option>
                            ))}
                        </select>
                        {fieldsError && (
                            <p className="mt-1 text-sm text-red-700" role="alert">{fieldsError}</p>
                        )}
                        {!fieldsLoading && !fieldsError && fields.length === 0 && (
                            <p className="mt-1 text-sm text-gray-600">
                                You have no assigned fields yet. Ask a manager to assign fields to your account.
                            </p>
                        )}
                        {selectedField && (
                            <p className="mt-1 text-xs text-gray-500">
                                {selectedField.crop_type ? `${selectedField.crop_type} • ` : ''}
                                {selectedField.area_hectares ? `${selectedField.area_hectares} ha • ` : ''}
                                {selectedField.farmer_name || 'No farmer linked'}
                            </p>
                        )}
                    </div>

                    {/* Visit Details */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Visit Date *</label>
                            <input
                                type="date"
                                value={formData.visit_date}
                                onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Visit Type *</label>
                            <select
                                value={formData.visit_type}
                                onChange={(e) => setFormData({ ...formData, visit_type: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            >
                                <option value="routine">Routine</option>
                                <option value="followup">Follow-up</option>
                                <option value="emergency">Emergency</option>
                                <option value="training">Training</option>
                            </select>
                        </div>
                    </div>

                    {/* Location */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                            <input
                                type="text"
                                value={formData.location_name}
                                onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                                placeholder="e.g., Plot A, North Field"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-sm font-medium text-gray-700">Latitude</label>
                                <button
                                    type="button"
                                    onClick={handleUseMyLocation}
                                    disabled={locating}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline disabled:opacity-50"
                                >
                                    <MapPinIcon className="w-3.5 h-3.5" />
                                    {locating ? 'Locating…' : 'Use my location'}
                                </button>
                            </div>
                            <input
                                type="number"
                                step="any"
                                value={formData.location_lat}
                                onChange={(e) => setFormData({ ...formData, location_lat: e.target.value })}
                                placeholder="-1.9403"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                            <input
                                type="number"
                                step="any"
                                value={formData.location_lng}
                                onChange={(e) => setFormData({ ...formData, location_lng: e.target.value })}
                                placeholder="29.8739"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                        </div>
                    </div>

                    {/* Weather */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Weather</label>
                            <select
                                value={formData.weather_condition}
                                onChange={(e) => setFormData({ ...formData, weather_condition: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            >
                                <option value="sunny">☀️ Sunny</option>
                                <option value="cloudy">☁️ Cloudy</option>
                                <option value="rainy">🌧️ Rainy</option>
                                <option value="stormy">⛈️ Stormy</option>
                                <option value="windy">💨 Windy</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Temp (°C)</label>
                            <input
                                type="number"
                                value={formData.temperature_celsius}
                                onChange={(e) => setFormData({ ...formData, temperature_celsius: e.target.value })}
                                placeholder="25"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Humidity (%)</label>
                            <input
                                type="number"
                                value={formData.humidity_percent}
                                onChange={(e) => setFormData({ ...formData, humidity_percent: e.target.value })}
                                placeholder="60"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                        </div>
                    </div>

                    {/* Crop Details */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Crop Type</label>
                            <input
                                type="text"
                                value={formData.crop_type}
                                onChange={(e) => setFormData({ ...formData, crop_type: e.target.value })}
                                placeholder="e.g., Maize, Beans"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Growth Stage</label>
                            <select
                                value={formData.growth_stage}
                                onChange={(e) => setFormData({ ...formData, growth_stage: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            >
                                <option value="seedling">Seedling</option>
                                <option value="vegetative">Vegetative</option>
                                <option value="flowering">Flowering</option>
                                <option value="fruiting">Fruiting</option>
                                <option value="harvest">Harvest</option>
                                <option value="post_harvest">Post-Harvest</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Health Score (0-100)</label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={formData.health_score}
                                onChange={(e) => setFormData({ ...formData, health_score: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Health Status *</label>
                        <select
                            value={formData.health_status}
                            onChange={(e) => setFormData({ ...formData, health_status: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                        >
                            <option value="excellent">Excellent</option>
                            <option value="good">Good</option>
                            <option value="fair">Fair</option>
                            <option value="poor">Poor</option>
                            <option value="critical">Critical</option>
                        </select>
                    </div>

                    {/* Pest & Disease */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.pest_observed}
                                    onChange={(e) => setFormData({ ...formData, pest_observed: e.target.checked })}
                                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                />
                                <span className="text-sm font-medium text-gray-700">Pest Observed</span>
                            </label>
                            {formData.pest_observed && (
                                <input
                                    type="text"
                                    value={formData.pest_type}
                                    onChange={(e) => setFormData({ ...formData, pest_type: e.target.value })}
                                    placeholder="Type of pest"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                                />
                            )}
                        </div>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.disease_observed}
                                    onChange={(e) => setFormData({ ...formData, disease_observed: e.target.checked })}
                                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                />
                                <span className="text-sm font-medium text-gray-700">Disease Observed</span>
                            </label>
                            {formData.disease_observed && (
                                <input
                                    type="text"
                                    value={formData.disease_type}
                                    onChange={(e) => setFormData({ ...formData, disease_type: e.target.value })}
                                    placeholder="Type of disease"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                                />
                            )}
                        </div>
                    </div>

                    {(formData.pest_observed || formData.disease_observed) && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Severity Level</label>
                            <select
                                value={formData.severity_level}
                                onChange={(e) => setFormData({ ...formData, severity_level: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                            </select>
                        </div>
                    )}

                    {/* Activities */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Activities Performed</label>
                        <div className="flex flex-wrap gap-2">
                            {ACTIVITIES.map((activity) => (
                                <button
                                    key={activity}
                                    type="button"
                                    onClick={() => toggleActivity(activity)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                        formData.activities.includes(activity)
                                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                            : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                                    }`}
                                >
                                    {activity}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Observations</label>
                        <textarea
                            value={formData.observations}
                            onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                            rows={3}
                            placeholder="Describe what you observed during the visit..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Recommendations</label>
                        <textarea
                            value={formData.recommendations}
                            onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                            rows={2}
                            placeholder="Any recommendations for the farmer..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Action Items</label>
                        <textarea
                            value={formData.action_items}
                            onChange={(e) => setFormData({ ...formData, action_items: e.target.value })}
                            rows={2}
                            placeholder="Follow-up actions needed..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none resize-none"
                        />
                    </div>

                    {/* Follow-up */}
                    <div className="space-y-2">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={formData.follow_up_required}
                                onChange={(e) => setFormData({ ...formData, follow_up_required: e.target.checked })}
                                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className="text-sm font-medium text-gray-700">Follow-up Required</span>
                        </label>
                        {formData.follow_up_required && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
                                    <input
                                        type="date"
                                        value={formData.follow_up_date}
                                        onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Notes</label>
                                    <input
                                        type="text"
                                        value={formData.follow_up_notes}
                                        onChange={(e) => setFormData({ ...formData, follow_up_notes: e.target.value })}
                                        placeholder="Notes for follow-up"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 flex items-start gap-2" role="alert">
                            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 shrink-0" />
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    )}
                </form>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-cyan-700 shadow-lg disabled:opacity-50 transition-all"
                    >
                        {loading ? 'Saving...' : initialData ? 'Update Entry' : 'Save Entry'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FieldDiaryForm;
